<script setup lang="ts">
import { isSessionPath } from "~/utils/session";

/**
 * The prompt shown when the tab is running a build that is no longer deployed.
 *
 * Asks rather than reloads, because the user may be mid-signature — the reload
 * only happens on its own once an import has failed, and never during a
 * session. It follows that the banner stays until it is acted on: no dismissal.
 * `CommonBanner`'s own dismissal is not it either, since that one remembers the
 * answer in localStorage forever, and this question comes back every deploy.
 *
 * Inside the session flows the merely-out-of-date notice is held back. The
 * signer is on camera looking at this screen, the notary is not going to reload
 * mid-notarization, and a warning nobody can act on only worries the room. It
 * appears again the moment they leave — or immediately, if something has
 * actually failed to load, because then it explains what just broke.
 *
 * @see composables/useAppUpdate
 */
const { updateAvailable, chunkFailed, reloadForUpdate } = useAppUpdate();

const route = useRoute();

const isVisible = computed(
  () => chunkFailed.value || (updateAvailable.value && !isSessionPath(route.path)),
);
</script>

<template>
  <CommonBanner
    v-if="isVisible"
    data-test-id="app-update-banner"
    bg-color="bg-info"
    :z-index="9999"
  >
    <span v-if="chunkFailed" data-test-id="app-update-broken-message">
      Part of the app could not be loaded because a newer version is live.
      Reload to fix it.
    </span>
    <span v-else>
      A new version of the app is available. If you don't update, you may
      experience issues.
    </span>
    <CommonButton
      data-test-id="app-update-reload-button"
      label="Reload now"
      type="minimal"
      color="white"
      label-class="underline"
      outer-class="!mt-0 !mb-0 !px-3 ml-2"
      @click="reloadForUpdate"
    />
  </CommonBanner>
</template>
